import { appointmentChecklistInstanceChecklistSchema, Guid } from '@breezy/shared'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'urql'
import { AppointmentChecklistInstanceForm } from '../../components/AppointmentChecklists/AppointmentChecklistInstanceForm'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import useIsMobile from '../../hooks/useIsMobile'
import { READONLY_CUSTOM_FORM_PAGE_QUERY } from './ReadonlyCustomFormPage.gql'

const NotFound = () => (
  <div className="flex h-screen w-screen items-center justify-center">
    <div className="text-center text-base font-semibold">
      The requested form cannot be found.
      <br />
      <br />
      If you believe you should be able to see this form, please request a fresh form link from your Pro.
    </div>
  </div>
)

const FailedToParse = () => (
  <div className="flex h-screen w-screen items-center justify-center">
    <div className="text-center text-base font-semibold">
      There was an error reading the form. Try again later.
      <br />
      <br />
      If this issue persists, reach out to your Pro.
    </div>
  </div>
)

type ReadonlyCustomFormPageWithGuidProps = {
  appointmentChecklistInstanceId: Guid
}

const ReadonlyCustomFormPageWithGuid = React.memo<ReadonlyCustomFormPageWithGuidProps>(
  ({ appointmentChecklistInstanceId }) => {
    const isMobile = useIsMobile()

    const [{ data: checklistData, fetching: fetchingChecklistData }] = useQuery({
      query: READONLY_CUSTOM_FORM_PAGE_QUERY,
      variables: {
        appointmentChecklistInstanceId,
      },
    })

    const parsedChecklist = useMemo(() => {
      if (!checklistData?.appointmentChecklistInstancesByPk) {
        return undefined
      }

      return appointmentChecklistInstanceChecklistSchema.safeParse(
        checklistData.appointmentChecklistInstancesByPk.checklist,
      )
    }, [checklistData?.appointmentChecklistInstancesByPk])

    if (fetchingChecklistData) {
      return (
        <div className="flex h-screen w-screen items-center justify-center">
          <LoadingSpinner />
        </div>
      )
    }
    if (!checklistData?.appointmentChecklistInstancesByPk) {
      return <NotFound />
    }
    if (!parsedChecklist?.success) {
      console.error(`Failed to parse checklist data. Checklist Instance ID: ${appointmentChecklistInstanceId}`)
      return <FailedToParse />
    }
    return (
      <div className="h-screen w-screen">
        <div className={classNames('m-auto max-w-[1000px]', isMobile ? 'p-4' : 'px-6 py-5')}>
          <div className={classNames('text-3xl font-semibold text-bz-gray-1000', isMobile ? 'pb-4' : 'pb-6')}>
            {parsedChecklist.data.name ?? 'Custom Form'}
          </div>
          <AppointmentChecklistInstanceForm items={parsedChecklist.data.items} />
        </div>
      </div>
    )
  },
)

export const ReadonlyCustomFormPage = React.memo(() => {
  const { appointmentChecklistInstanceId } = useParams<{
    appointmentChecklistInstanceId?: string
  }>()

  if (!appointmentChecklistInstanceId) {
    return <NotFound />
  }
  const isValidGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    appointmentChecklistInstanceId,
  )
  if (!isValidGuid) {
    return <NotFound />
  }

  return <ReadonlyCustomFormPageWithGuid appointmentChecklistInstanceId={appointmentChecklistInstanceId} />
})

ReadonlyCustomFormPage.displayName = 'ReadonlyCustomFormPage'
